import React, { useState } from 'react';
import {
    doc,
    updateDoc
} from 'firebase/firestore';
import { db } from '../../../Services/firebase-config';
import { useAuth } from '../../../Contexts/AuthContext';
import {
    Users,
    BookOpen,
    Search,
    Filter,
    Mail,
    Phone,
    User
} from 'lucide-react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './calendar-custom.css';
import ResourceCard from "./ResourceCard";
import ResourceViewer from "./ResourceViewer";

const Resources = ({
    event,
    selectedDate,
    setSelectedDate,
    expandedResource,
    setExpandedResource,
    currentTeam,
    mentorResources = [],
    onSwitchToTeams,
    onTeamUpdate
}) => {
    const { currentUser } = useAuth();
    const [loading] = useState(false);
    const [viewingResource, setViewingResource] = useState(null);
    const [markingResources, setMarkingResources] = useState(new Set());

    // Helper function to format date for resources data lookup
    const formatDateForLookup = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Helper function to check if date has resources
    const hasResourcesOnDate = (date) => {
        if (!mentorResources?.length) return false;
        const dateKey = formatDateForLookup(date);
        return mentorResources.some(resource => resource.date === dateKey);
    };

    // Custom tile content to show resource indicators
    const tileContent = ({ date, view }) => {
        if (view === 'month' && hasResourcesOnDate(date)) {
            return <div className="absolute bottom-1 right-1 w-2 h-2 bg-custom-green-1 rounded-full"></div>;
        }
        return null;
    };

    // Custom tile class name for styling
    const tileClassName = ({ date, view }) => {
        if (view === 'month') {
            const today = new Date();
            const isToday = date.toDateString() === today.toDateString();
            const isSelected = date.toDateString() === selectedDate.toDateString();

            if (isSelected) {
                return 'react-calendar__tile--selected';
            }
            if (isToday) {
                return 'react-calendar__tile--today';
            }
            if (hasResourcesOnDate(date)) {
                return 'react-calendar__tile--has-resources';
            }
        }
        return null;
    };

    const handleViewResource = (resourceData) => {
        setViewingResource(resourceData);
    };

    const handleBackToList = () => {
        setViewingResource(null);
    };

    const handleMarkResourceAsCompleted = async (resourceId) => {
        if (!currentUser?.uid) return;

        try {
            setMarkingResources(prev => new Set([...prev, resourceId]));

            const resourceRef = doc(db, 'resources', resourceId);
            const resource = mentorResources.find(r => r.id === resourceId);

            if (!resource) {
                throw new Error('Resource not found');
            }

            // Get current readBy array or initialize it
            const currentReadBy = resource.readBy || [];

            // Add user if not already in the array
            if (!currentReadBy.includes(currentUser.uid)) {
                await updateDoc(resourceRef, {
                    readBy: [...currentReadBy, currentUser.uid],
                });

                // Update local state through the parent component
                if (onTeamUpdate) {
                    await onTeamUpdate();
                }
            }
        } catch (error) {
            console.error('Error marking resource as completed:', error);
        } finally {
            setMarkingResources(prev => {
                const updated = new Set(prev);
                updated.delete(resourceId);
                return updated;
            });
        }
    };

    const selectedDateKey = formatDateForLookup(selectedDate);

    return (
        <div className="space-y-4 sm:space-y-8">
            {viewingResource ? (
                // Resource Viewer Mode
                <ResourceViewer
                    resource={viewingResource}
                    onBack={handleBackToList}
                />
            ) : (
                // Normal Calendar and Resources Mode
                <>
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg sm:text-2xl font-bold text-gray-800">Learning Calendar</h2>
                        {currentTeam && (
                            <div className="text-xs sm:text-sm text-gray-600">
                                <span className="font-medium">Team:</span> {currentTeam.name}
                            </div>
                        )}
                    </div>

                    {!currentTeam ? (
                        <div className="text-center py-8 sm:py-12">
                            <Users className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-3 sm:mb-4" />
                            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No Team Assigned</h3>
                            <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 px-4">Join or create a team to access team-specific resources and mentorship.</p>
                            <button
                                onClick={() => onSwitchToTeams && onSwitchToTeams()}
                                className="inline-flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-custom-blue-1 text-white rounded-lg hover:bg-custom-blue-1/90 transition-colors font-medium text-sm sm:text-base"
                            >
                                <Users className="w-4 h-4 sm:w-5 sm:h-5" />
                                Create or Join Team
                            </button>
                        </div>
                    ) : (
                        <>
                            {/* Calendar */}
                            <div className="bg-white rounded-xl shadow-sm border p-3 sm:p-6 mb-4 sm:mb-6">
                                <div className="calendar-container">
                                    <Calendar
                                        onChange={setSelectedDate}
                                        value={selectedDate}
                                        tileContent={tileContent}
                                        tileClassName={tileClassName}
                                        className="react-calendar-custom"
                                        navigationLabel={({ date }) => `${date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`}
                                        next2Label={null}
                                        nextLabel={null}
                                        prev2Label={null}
                                        prevLabel={null}
                                        showNavigation={false}
                                    />
                                </div>
                            </div>

                            {/* Only show resources section if there are resources available */}
                            {mentorResources?.length > 0 && (
                                <>
                                    {/* Separator Line */}
                                    <div className="border-t border-gray-300 my-8 sm:my-16"></div>

                                    {/* Resources for Selected Date */}
                                    <div className="mb-6 sm:mb-12">
                                        <div className="flex items-center justify-between mb-6">
                                            <h3 className="text-xl font-bold text-gray-800">
                                                Resources for {selectedDate.toLocaleDateString()}
                                            </h3>
                                            <div className="flex items-center gap-3">
                                                <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                                                    <Filter className="w-4 h-4" />
                                                    Filter
                                                </button>
                                                <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                                                    <Search className="w-4 h-4" />
                                                    Search
                                                </button>
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            {mentorResources
                                                .filter(resource => resource.date === formatDateForLookup(selectedDate))
                                                .length > 0 ? (
                                                mentorResources
                                                    .filter(resource => resource.date === formatDateForLookup(selectedDate))
                                                    .map(resource => (
                                                        <ResourceCard
                                                            key={resource.id}
                                                            resource={resource}
                                                            isExpanded={expandedResource === resource.id}
                                                            onToggle={() => setExpandedResource(expandedResource === resource.id ? null : resource.id)}
                                                            onViewResource={handleViewResource}
                                                            onMarkAsCompleted={handleMarkResourceAsCompleted}
                                                            currentUserId={currentUser?.uid}
                                                            isMarking={markingResources.has(resource.id)}
                                                        />
                                                    ))
                                            ) : (
                                                <div className="text-center py-12">
                                                    <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                                    <p className="text-gray-500">No resources available for this date</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Team Mentor Section */}
                            {currentTeam?.mentor && (
                                <>
                                    {/* Separator Line */}
                                    <div className="border-t border-gray-300 my-16"></div>

                                    <div className="bg-white rounded-xl shadow-sm border p-6 mb-8">
                                        <h3 className="text-xl font-bold text-gray-800 mb-6">Your Team Mentor</h3>
                                        <div className="bg-gray-50 rounded-lg p-6">
                                            <div className="flex items-start gap-4">
                                                <div className="w-16 h-16 bg-custom-blue-1/10 rounded-full flex items-center justify-center flex-shrink-0">
                                                    {currentTeam.mentor.photoUrl && currentTeam.mentor.photoUrl.trim() !== "" ? (
                                                        <img
                                                            src={currentTeam.mentor.photoUrl}
                                                            alt={currentTeam.mentor.name}
                                                            className="w-16 h-16 rounded-full object-cover"
                                                        />
                                                    ) : (
                                                        <User className="w-8 h-8 text-custom-blue-1" />
                                                    )}

                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="font-semibold text-gray-900 text-lg">{currentTeam.mentor.name}</h4>
                                                    <div className="mt-2 space-y-2">
                                                        <div className="flex items-center gap-2 text-gray-600">
                                                            <Mail className="w-4 h-4" />
                                                            <span className="text-sm">{currentTeam.mentor.email}</span>
                                                        </div>
                                                        {currentTeam.mentor.phoneNumber && (
                                                            <div className="flex items-center gap-2 text-gray-600">
                                                                <Phone className="w-4 h-4" />
                                                                <span className="text-sm">{currentTeam.mentor.phoneNumber}</span>
                                                            </div>
                                                        )}
                                                        <div className="text-xs text-gray-500 mt-3">
                                                            Assigned on: {currentTeam?.mentor?.assignedAt
                                                                ? currentTeam.mentor.assignedAt.toDate().toLocaleDateString("en-GB", {
                                                                    day: "numeric",
                                                                    month: "short",
                                                                    year: "numeric",
                                                                })
                                                                : "Unknown"}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Available Mentors & Tools Section */}
                            <div className="grid lg:grid-cols-2 gap-8 mb-8">
                                {/* Available Mentors */}
                                <div className="bg-white rounded-xl shadow-sm border p-6">
                                    <h3 className="text-xl font-bold text-gray-800 mb-6">Available Mentors</h3>
                                    <div className="space-y-4">
                                        {loading ? (
                                            // Skeleton loading for mentor assignment status
                                            <div className="animate-pulse">
                                                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                                    <div className="w-10 h-10 bg-gray-300 rounded-full flex-shrink-0"></div>
                                                    <div className="flex-1 min-w-0 space-y-2">
                                                        <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                                                        <div className="h-3 bg-gray-300 rounded w-1/2"></div>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : currentTeam?.mentorId ? (
                                            <div className="text-center py-8">
                                                <Users className="w-12 h-12 text-custom-green-1 mx-auto mb-4" />
                                                <p className="text-custom-green-1 font-semibold">✓ Mentor Assigned</p>
                                                <p className="text-gray-600 text-sm mt-2">
                                                    Your team has been assigned a mentor who will guide you throughout the event.
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="text-center py-8">
                                                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                                <p className="text-gray-500 font-semibold">⏳ Awaiting Mentor Assignment</p>
                                                <p className="text-gray-600 text-sm mt-2">
                                                    A mentor will be assigned to your team soon. Check back later.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default Resources;